import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { GetConversationFilesQueryFile } from "#types/types.ts";
import { useQuery } from "@tanstack/react-query";

export const POLL_INTERVAL_MS = 1500;

// Keep polling until every requested file has reached a terminal availability — "Available" or
// "Failed". A missing file (brief lag right after complete-upload) counts as "still processing".
// There is deliberately no client-side time budget: the server owns the only timeout (a 10-minute
// grace window from upload before a stalled file is flipped to "Failed"), so this just polls until
// availability is terminal. A stuck file can be removed by the user; the query stops once it leaves
// the pending set.
export const shouldKeepPolling = (
    data: GetConversationFilesQueryFile[] | undefined,
    requestedCount: number
): boolean => {
    if (!data || data.length < requestedCount) {
        return true;
    }

    return data.some(file => file.availability === "Processing");
};

interface Options {
    enabled: boolean;
}

export const useGetConversationFilesPollingQuery = (fileIds: string[], { enabled }: Options) => {
    const { getConversationFiles } = useAiApiClient();

    return useQuery({
        queryKey: AiQueryKeys.GetConversationFiles(fileIds),
        queryFn: () => getConversationFiles(fileIds),
        enabled: enabled && fileIds.length > 0,
        // Inherit the global retry policy (429 / network, with backoff) like every other query. Keep the
        // interval running even after a request error: there is no client budget, so stopping here would
        // trap the send-gate forever on a transient failure (the file stays "Processing" until the
        // server's grace window flips it to "Failed"). Re-polling lets a transient failure self-heal; a
        // persistently stuck file is escaped by removing it.
        refetchInterval: query =>
            (shouldKeepPolling(query.state.data, fileIds.length) ? POLL_INTERVAL_MS : false)
    });
};
